export type TopologyNode = {
  id: string;
  label: string;
  kind?: string; // e.g., "Pump", "Motor", "Compressor"
};

export type TopologyStripProps = {
  nodes: TopologyNode[];
  focusId: string;
  impactedIds?: string[];
};

/**
 * Left-to-right chain of upstream → asset → downstream nodes. Focused
 * asset rendered with stronger contrast; impacted downstream neighbors
 * pick up a warn tint. Arrow glyphs are aria-hidden — the connection
 * shape is conveyed via the ordered list semantics.
 */
export function TopologyStrip({
  nodes,
  focusId,
  impactedIds = [],
}: TopologyStripProps) {
  return (
    <ol
      aria-label="Asset topology"
      className="inline-flex items-center gap-sp-2 overflow-x-auto"
    >
      {nodes.map((n, i) => {
        const isFocus = n.id === focusId;
        const isImpacted = impactedIds.includes(n.id);
        return (
          <li key={n.id} className="inline-flex items-center gap-sp-2">
            <span
              data-focus={isFocus || undefined}
              data-impacted={isImpacted || undefined}
              className={
                'inline-flex flex-col items-start rounded-r-default border px-sp-3 py-sp-2 ' +
                (isFocus
                  ? 'border-gray-900 bg-gray-50 text-gray-900'
                  : isImpacted
                    ? 'border-warn bg-warn-bg text-gray-900'
                    : 'border-gray-300 bg-gray-50 text-gray-700')
              }
            >
              <span className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
                {n.kind ?? 'Node'}
              </span>
              <span className="text-fs-body">{n.label}</span>
            </span>
            {i < nodes.length - 1 ? (
              <span aria-hidden className="text-gray-700">
                →
              </span>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
